import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import search from 'hooks/grids/search';

/**
 * Best-effort fetch of the raw bonus dataset record's `driverType` roster value
 * (e.g. "OE" for owner-operator). This value lives on the raw dataset record,
 * not on the reward output, so we look it up by the record id via the generic
 * `search2` query (filtering on the `id` keyword field — the established
 * single-record pattern).
 *
 * Returns `undefined` on any miss (no record, no field, or a failed query) so
 * the embed degrades gracefully: the host simply omits `driverType` from
 * EMBED_INIT and the driver app keeps its OE toggle hidden.
 */
const getBonusRecordDriverType = async ({
  client,
  dataType,
  bonusDatasetRecordId,
  startDate,
  endDate,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  // The bonus program's output dataset (BonusProgram.dataset).
  dataType: string;
  bonusDatasetRecordId: string;
  startDate: string;
  endDate: string;
}): Promise<string | undefined> => {
  try {
    const { documents } = await search({
      dataType,
      filters: [
        { keywords: [{ field: 'id', values: [bonusDatasetRecordId] }] },
      ],
      dateScope: { startDate, endDate },
      sortBy: [],
      client,
      includeFields: ['id', 'driverType'],
    });

    const driverType = documents[0]?.driverType;
    return typeof driverType === 'string' ? driverType : undefined;
  } catch {
    // Never block the embed on this lookup.
    return undefined;
  }
};

export default getBonusRecordDriverType;
